"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { useSession } from "@/contexts/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Mail from "lucide-react/dist/esm/icons/mail";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import SocialAuthButtons from "@/components/auth/SocialAuthButtons";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordInput from "@/components/auth/PasswordInput";
import {
  AuthFormPanel,
  AuthSkipLink,
  AuthFieldShell,
} from "@/components/auth/auth-form-ui";
import { FormAnticipationBar } from "@/components/ui/loading-anticipation";
import { trackLogin } from "@/lib/analytics";
import { trackAuthSigninCompleted } from "@/lib/analytics/auth-tracking";
import { reportErrorToDiscord, toUserFriendlyAuthError } from "@/lib/error-reporting";
import { ResendVerification } from "@/components/auth/ResendVerification";
import { cn } from "@/lib/utils";
import { consumePendingIntent, peekPendingIntent } from "@/lib/pending-intent";
import { safeRedirectPath } from "@/lib/safe-redirect";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [showResend, setShowResend] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const { signIn } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const from = safeRedirectPath(searchParams.get("redirect")) ?? "/";

  const clearFieldError = (key: "email" | "password") => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateFields = () => {
    const next: { email?: string; password?: string } = {};
    const trimmed = email.trim();
    if (!trimmed) next.email = "Enter your email.";
    else if (!EMAIL_RE.test(trimmed)) next.email = "Enter a valid email address.";
    if (!password) next.password = "Enter your password.";
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFields()) return;

    if (isTurnstileEnabled() && !captchaToken) {
      setFieldErrors({ password: "Please complete the verification challenge." });
      return;
    }

    setLoading(true);
    setShowResend(false);

    const { error: signInError, userId } = await signIn(email.trim(), password, captchaToken ?? undefined);

    if (signInError) {
      // Turnstile tokens are single-use — force a fresh challenge for retry.
      setCaptchaToken(null);
      setTurnstileKey((k) => k + 1);
      const msg = signInError.message;
      const isInvalidLogin = msg.includes("Invalid login");
      const isUnconfirmed = msg.includes("Email not confirmed");

      const userMessage = isUnconfirmed
        ? "Verify your email before signing in."
        : isInvalidLogin
          ? "Invalid email or password."
          : toUserFriendlyAuthError(msg);

      if (isUnconfirmed) {
        setFieldErrors({ email: userMessage });
        setShowResend(true);
      } else if (isInvalidLogin) {
        setFieldErrors({ password: userMessage });
      } else {
        setFieldErrors({ password: userMessage });
      }

      reportErrorToDiscord({
        source: "SignIn.submit",
        userMessage,
        error: signInError,
      });

      setLoading(false);
      return;
    }

    if (userId) {
      trackAuthSigninCompleted("email", userId);
    }
    trackLogin("email");

    // A "go here next" intent saved at sign-up (e.g. "Apply to sell" →
    // seller onboarding) beats the redirect param. Consuming it here, rather
    // than leaving it to PendingIntentResumer, keeps the two from racing two
    // navigations. Every other intent kind is the resumer's to finish.
    const intent = peekPendingIntent();
    const intentPath = intent?.kind === "other" ? safeRedirectPath(intent.returnTo) : null;
    if (intentPath) {
      consumePendingIntent();
      router.replace(intentPath);
    } else {
      router.replace(from);
    }
  };

  return (
    <>
      <AuthLayout heading="Welcome back" subheading="Sign in to reach your downloads, library and wishlist.">
        <AuthSkipLink href="#signin-email-form">Skip to email sign-in</AuthSkipLink>
        <AuthFormPanel className="animate-fade-up stagger-1 space-y-4 p-5">
          <SocialAuthButtons mode="signin" />

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-card/80 px-3 text-muted-foreground font-mono-accent tracking-[0.2em]">or</span>
            </div>
          </div>

          <form
            id="signin-email-form"
            onSubmit={(e) => void handleSubmit(e)}
            aria-label="Sign in with email"
            className="space-y-3"
          >
            <AuthFieldShell>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearFieldError("email");
                  }}
                  autoComplete="email"
                  autoFocus
                  aria-invalid={!!fieldErrors.email}
                  className={cn(
                    "pl-10 bg-secondary border-0",
                    fieldErrors.email && "border border-destructive/80 focus-visible:ring-destructive/30",
                  )}
                />
              </div>
              {fieldErrors.email && (
                <p className="text-xs text-destructive" role="status">
                  {fieldErrors.email}
                </p>
              )}
              {showResend && email.trim() && (
                <div className="pt-1">
                  <ResendVerification email={email.trim()} />
                </div>
              )}
            </AuthFieldShell>

            <AuthFieldShell>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearFieldError("password");
                }}
                autoComplete="current-password"
                aria-invalid={!!fieldErrors.password}
                className={cn(fieldErrors.password && "border border-destructive/80 focus-visible:ring-destructive/30")}
              />
              {fieldErrors.password && (
                <p className="text-xs text-destructive" role="status">
                  {fieldErrors.password}
                </p>
              )}
              <p className="text-right">
                <Link href="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </p>
            </AuthFieldShell>

            <TurnstileWidget key={turnstileKey} onToken={setCaptchaToken} className="flex justify-center" />

            <FormAnticipationBar show={loading} label="Signing in" />

            <Button
              type="submit"
              className="w-full h-11 text-[15px] font-semibold"
              disabled={loading || (isTurnstileEnabled() && !captchaToken)}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </AuthFormPanel>

        <div className="animate-fade-up stagger-2 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link href="/signup" className="font-semibold text-primary hover:underline">
            Create a free account
          </Link>
        </div>
      </AuthLayout>
    </>
  );
}
